// Generated by GitHub Copilot
// Builds Chart.js configs from the merged dashboard sections. Chart colours use
// fixed palette constants (as the template does) and are not re-themed by brand.
import { PALETTE as C } from './Chart';

const DFLT = { responsive: true, maintainAspectRatio: false, plugins: { legend: { display: false } } };
const legendBottom = { legend: { display: true, position: 'bottom', labels: { boxWidth: 10, font: { size: 10 } } } };
const legendRight = { legend: { display: true, position: 'right', labels: { boxWidth: 10, font: { size: 10 }, padding: 8 } } };

export function buildChartConfigs(sections) {
  const ov = sections.overview || {};
  const sc = sections.scope || {};
  const ta = sections.talent || {};
  const de = sections.delivery || {};
  const co = sections.cost || {};
  const va = sections.value || {};
  const di = sections.digital || {};

  const growth = ov.growth_chart || {};
  const growthColors = (growth.data || []).map((_, i, arr) => (i === arr.length - 1 ? C.RED : C.P600));

  const pipeline = sc.pipeline_chart || {};
  const attr = ta.attrition_chart || {};
  const comp = ta.composition_chart || {};
  const incident = de.incident_chart || {};
  const opex = co.opex_chart || {};
  const savings = co.savings_chart || {};
  const valueMix = va.value_mix_chart || {};
  const bc = va.bc_chart || {};
  const skilling = di.skilling_chart || {};

  return {
    growth: {
      type: 'bar',
      data: { labels: growth.labels || [], datasets: [{ data: growth.data || [], backgroundColor: growthColors, borderRadius: 4 }] },
      options: { ...DFLT, plugins: { ...DFLT.plugins, tooltip: { callbacks: { label: (c) => c.raw.toLocaleString() + ' FTEs' } } },
        scales: { y: { grid: { color: '#F3F4F6' }, ticks: { callback: (v) => (v >= 1000 ? v / 1000 + 'K' : v) }, beginAtZero: true }, x: { grid: { display: false } } } },
    },
    migration: {
      type: 'doughnut',
      data: { labels: pipeline.labels || [], datasets: [{ data: pipeline.data || [], backgroundColor: [C.GREEN, C.P500, C.AMBER, C.GREY], borderWidth: 0, hoverOffset: 6 }] },
      options: { ...DFLT, cutout: '60%', plugins: legendRight },
    },
    attrition: {
      type: 'line',
      data: { labels: attr.labels || [], datasets: [
        { label: 'Actual %', data: attr.actual || [], borderColor: C.RED, backgroundColor: 'rgba(227,41,52,.08)', fill: true, tension: .4, pointRadius: 3, borderWidth: 2 },
        { label: 'Benchmark', data: attr.benchmark || [], borderColor: C.GREY, borderDash: [4, 4], pointRadius: 0, fill: false },
      ] },
      options: { ...DFLT, plugins: legendBottom, scales: { y: { min: 12, max: 21, grid: { color: '#F3F4F6' }, ticks: { callback: (v) => v + '%' } }, x: { grid: { display: false } } } },
    },
    funcMix: {
      type: 'bar',
      data: { labels: comp.labels || [], datasets: [{ data: comp.data || [], backgroundColor: [C.P700, C.P500, C.RED, C.GREEN, C.AMBER, C.PURPLE], borderRadius: 4 }] },
      options: { ...DFLT, indexAxis: 'y', plugins: { ...DFLT.plugins, tooltip: { callbacks: { label: (c) => c.raw + '% of workforce' } } },
        scales: { x: { max: 30, grid: { color: '#F3F4F6' }, ticks: { callback: (v) => v + '%' } }, y: { grid: { display: false } } } },
    },
    incident: {
      type: 'line',
      data: { labels: incident.labels || [], datasets: [
        { label: 'P1', data: incident.p1 || [], borderColor: C.RED, backgroundColor: 'rgba(227,41,52,.06)', fill: true, tension: .4, pointRadius: 3, borderWidth: 2 },
        { label: 'P2', data: incident.p2 || [], borderColor: C.AMBER, backgroundColor: 'rgba(184,92,0,.06)', fill: true, tension: .4, pointRadius: 3, borderWidth: 2 },
      ] },
      options: { ...DFLT, plugins: legendBottom, scales: { y: { grid: { color: '#F3F4F6' } }, x: { grid: { display: false } } } },
    },
    opex: {
      type: 'doughnut',
      data: { labels: opex.labels || [], datasets: [{ data: opex.data || [], backgroundColor: [C.P800, C.P500, C.P400, C.AMBER, C.GREY], borderWidth: 0, hoverOffset: 6 }] },
      options: { ...DFLT, cutout: '60%', plugins: legendRight },
    },
    savings: {
      type: 'bar',
      data: { labels: savings.labels || [], datasets: [
        { label: 'Labour Arbitrage ($M)', data: savings.labour_arbitrage || [], backgroundColor: C.P600, borderRadius: 4, stack: 's' },
        { label: 'AI & Automation ($M)', data: savings.ai_automation || [], backgroundColor: C.RED, borderRadius: 4, stack: 's' },
      ] },
      options: { ...DFLT, plugins: legendBottom, scales: { x: { stacked: true, grid: { display: false } }, y: { stacked: true, grid: { color: '#F3F4F6' }, ticks: { callback: (v) => '$' + v + 'M' } } } },
    },
    valueMix: {
      type: 'doughnut',
      data: { labels: valueMix.labels || [], datasets: [{ data: valueMix.data || [], backgroundColor: [C.P800, C.P500, C.RED, C.AMBER], borderWidth: 0, hoverOffset: 6 }] },
      options: { ...DFLT, cutout: '58%', plugins: legendRight },
    },
    bc: {
      type: 'line',
      data: { labels: bc.labels || [], datasets: [
        { label: 'Business Case ($M)', data: bc.business_case || [], borderColor: C.GREY, borderDash: [5, 4], borderWidth: 1.5, fill: false, pointRadius: 3 },
        { label: 'Actual / Projected ($M)', data: bc.actuals || [], borderColor: C.RED, backgroundColor: 'rgba(227,41,52,.07)', fill: true, tension: .3, pointRadius: 4, borderWidth: 2 },
      ] },
      options: { ...DFLT, plugins: legendBottom, scales: { y: { grid: { color: '#F3F4F6' }, ticks: { callback: (v) => '$' + v + 'M' } }, x: { grid: { display: false } } } },
    },
    skilling: {
      type: 'doughnut',
      data: { labels: skilling.labels || [], datasets: [{ data: skilling.data || [], backgroundColor: [C.P600, C.P400, '#F3F4F6'], borderWidth: 0 }] },
      options: { ...DFLT, cutout: '65%', plugins: legendRight },
    },
  };
}
